<template>
    <div>
        <template v-for="(item, index) in items" :key="index">
            <div v-if="item.type === 'divider'" :key="index" class="divider" />
            <menu-item
                v-else
                :icon="item.icon"
                :title="item.title"
                :action="item.action"
                :isActive="item.isActive"
            />
        </template>
    </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import MenuItem from '@/components/html-editor/MenuItem.vue'
import { Editor } from '@tiptap/vue-3'
import { ref } from 'vue'

const { editor } = defineProps<{
    editor: Editor
}>()

const { t } = useI18n()

const items = ref([
    {
        icon: 'bold',
        title: t('bold') || 'Bold',
        action: () => editor.chain().focus().toggleBold().run(),
        isActive: () => editor.isActive('bold'),
    },
    {
        icon: 'italic',
        title: t('italic') || 'Italic',
        action: () => editor.chain().focus().toggleItalic().run(),
        isActive: () => editor.isActive('italic'),
    },
    {
        icon: 'strikethrough',
        title: t('strike') || 'Strike',
        action: () => editor.chain().focus().toggleStrike().run(),
        isActive: () => editor.isActive('strike'),
    },
    {
        type: 'divider',
    },
    {
        icon: 'list-ul',
        title: t('bullet_list') || 'Bullet List',
        action: () => editor.chain().focus().toggleBulletList().run(),
        isActive: () => editor.isActive('bulletList'),
    },
    {
        icon: 'list-ol',
        title: t('ordered_list') || 'Ordered List',
        action: () => editor.chain().focus().toggleOrderedList().run(),
        isActive: () => editor.isActive('orderedList'),
    },
    {
        icon: 'link',
        title: t('link') || 'Link',
        action: () => {
            const previousUrl = editor.getAttributes('link').href
            const url = window.prompt('URL', previousUrl || '')
            if (url === null) {
                // User cancelled the prompt, leave the link untouched:
                return
            }
            if (url === '') {
                editor.chain().focus().extendMarkRange('link').unsetLink().run()
            } else {
                editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
            }
        },
        isActive: () => editor.isActive('link'),
    },
    {
        type: 'divider',
    },
    {
        icon: 'remove-format',
        title: t('clear_format') || 'Clear Format',
        action: () => editor.chain().focus().clearNodes().unsetAllMarks().run(),
    },
    {
        type: 'divider',
    },
    {
        icon: 'undo',
        title: t('undo') || 'Undo',
        action: () => editor.chain().focus().undo().run(),
    },
    {
        icon: 'redo',
        title: t('redo') || 'Redo',
        action: () => editor.chain().focus().redo().run(),
    },
])
</script>

<style lang="scss" scoped>
.divider {
    width: 2px;
    height: 1.25rem;
    margin-left: 0.5rem;
    margin-right: 0.75rem;
    background-color: #AAA;
}
</style>
